import { Injectable } from '@angular/core';
import * as Sentry from '@sentry/angular';
import { AppEnvironment } from 'environments/environment';
import { isAppStandalone, isBrowserSafari, isCapacitorNative, isPlatformIOS } from 'environments/environment-helpers';
import { BOOT_LOCALE } from 'i18n/i18n.service';
import { AppVersion } from 'app/version';
import { classifyBootFreeze } from 'app/boot-freeze-classifier';
import { LoggingService, errorReportFields } from 'services/logging.service';
import { SessionHealth } from 'services/session-health';
import { ConnectionState, OnlineStateService } from 'services/online-state.service';
import { whenBackendRouteSettledAsync } from 'util/backend-origin';
import { I18N_FALLBACK_STATS } from '../../gensrc/i18n/i18n-fallback-stats';

/**
 * The boot-time reporters that only OBSERVE and report, lifted out of
 * `AppComponent` (third slice of deferred finding df-7b66acbeb341).
 *
 * What every method in here has in common, and what decided the boundary: none
 * of them changes what the app does, none of them is awaited by the boot
 * sequence, and none of them is read back by anything later in the boot. They
 * look at something that already happened — a previous boot's watchdog log, a
 * service-worker registration that failed before Angular started, the protocol
 * the CouchDB connection negotiated — and send it to Sentry.
 *
 * WHERE each one is called from still belongs to `AppComponent`, and every call
 * site stayed exactly where it was. That is not a formality: the boot order is a
 * documented contract (`design-docs/boot-sequence.md`), and a few of these read
 * state that only exists once an earlier boot step has run. So the bodies moved
 * and the sequencing did not.
 *
 * DELIBERATELY LEFT BEHIND: the web-boot-marker pair,
 * `guardedWebBootMarkerWrite` and `reportPreviousWebBootIncompleteIfAny`. They
 * sit next to these in the component and read like reporters, but both are
 * driven from the `bootStage` setter and must run in a precise order relative to
 * the first stage transition. They are boot machinery wearing a reporter's
 * clothes.
 *
 * Why this is one file rather than the plain-decisions / Angular-wiring pair the
 * earlier slices of this finding used, and what a later slice should take from
 * that: `design-docs/deferred-findings/2026-07-27T1117Z-refactor-vs-extend-steering.md`,
 * section 9.
 */
@Injectable({ providedIn: 'root' })
export class BootDiagnosticsReportersService {
  constructor(
    private logging: LoggingService,
    private onlineStateService: OnlineStateService,
  ) { }

  // Native prod-bundle backend-routing telemetry (#4280, #4405). Records which
  // server this session talks to (apex/canary), the versions that drove the
  // choice, and how the value arrived (cache / probed / pending / unknown).
  // This lets a future App Review — or a #4273-style lockout report — be
  // verified from data instead of re-diagnosed. Routing itself is owned by
  // getApiOriginAsync(); this only reports. No-op off the native prod bundle
  // (web, and the dev/local native bundles).
  //
  // #4405 made this WAIT for the route before reporting. Before, the native
  // side held the first boot until the route was known, so by the time this ran
  // it could not be unresolved. Now the page loads immediately, so on a
  // first-ever boot this can run while the probe is still in flight — and
  // reporting the pre-#4405 tolerant apex default there would publish a route
  // the session may never actually use.
  //
  // Fire-and-forget: nothing downstream waits on the tags, and
  // whenBackendRouteSettledAsync never throws, so this cannot affect boot.
  reportBackendRouteTelemetry(): void {
    void whenBackendRouteSettledAsync().then(info => {
      if (!info) return; // not the native prod bundle — nothing routed at runtime
      // if the route never arrived but we believe we ARE online, that is a bug,
      // not an offline user. Report it, because nothing else will: every caller
      // that gives up on the wait fails as a network error by design, so the
      // user is told "no connection" and a genuinely broken publish path (a
      // WebView with no DOCUMENT_START_SCRIPT support, a failed injection)
      // would otherwise look exactly like being offline and reach no one.
      if (info.route === undefined && this.onlineStateService.isLikelyOnline()) {
        this.logging.trackSilentProblem('BackendRouteNeverResolvedWhileOnline', {
          source: info.source,
          clientVersion: info.clientVersion,
        });
      }
      // status line — becomes a Sentry breadcrumb on any later error this session.
      // route is undefined when the probe never answered at all (source='pending'),
      // which on this path means the device stayed offline for the whole wait.
      console.log(
        'Backend route: routed_to=%s client=%s live=%s source=%s',
        info.route ?? 'unresolved', info.clientVersion, info.liveVersion, info.source,
      );
      // tag every subsequent Sentry event in the session with the routing context
      Sentry.setTag('routed_to', info.route ?? 'unresolved');
      Sentry.setTag('backend_route_source', info.source);
    }).catch(err => {
      // whenBackendRouteSettledAsync never rejects, so reaching here means the
      // callback above threw — surface it rather than letting it become an
      // unhandled rejection nobody sees
      this.logging.trackSilentProblem('BackendRouteTelemetryFailed', errorReportFields(err));
    });
  }

  async startNetworkLoggerAsync() {
    // iOS-only Capacitor plugin (NWPathMonitor wrapper); calling start() on Android throws.
    if (!isCapacitorNative() || !isPlatformIOS()) return;
    try {
      const { registerPlugin } = await import('@capacitor/core');
      const CsNetworkLoggerPlugin = registerPlugin<{ start(): Promise<void>; addListener(event: string, cb: (data: any) => void): any }>('CsNetworkLoggerPlugin');
      await CsNetworkLoggerPlugin.start();
      CsNetworkLoggerPlugin.addListener('networkPathChanged', (data: any) => {
        console.log(`[NWPath] status=${data.status} interfaces=${data.interfaces} expensive=${data.expensive} constrained=${data.constrained}`);
      });
    } catch (error) {
      this.logging.trackSilentProblem('CsNetworkLogger:StartFailed', { error: error?.message || String(error) });
    }
  }

  reportSwRegistrationError() {
    const swError = (window as any).__swRegistrationError;
    if (swError) {
      delete (window as any).__swRegistrationError;
      // bots can't register service workers — not actionable
      if (/Google-Read-Aloud|Googlebot|GoogleOther|PlayStore|compatible;\s*bot|TikTok/i.test(swError.userAgent || '')) {
        console.warn('SW: Registration failed (bot, ignored):', swError.userAgent);
        return;
      }
      swError.isStandalone = isAppStandalone();
      swError.hasExistingController = !!navigator.serviceWorker?.controller;
      this.logging.trackSilentProblem('ServiceWorkerRegistrationFailed', swError);
    }
  }

  // #4147 safety net: report when a NON-English build shipped with any
  // untranslated UI string. The generated i18n-fallback-stats artifact records,
  // per locale, how many keys fell back to English at build time — the only
  // place that fact still exists (the merged runtime catalog bakes English in,
  // so it is indistinguishable from a real translation at runtime). The CI /
  // push / review gate already blocks missing + stale keys, so in a normal
  // release this count is zero and nothing fires. It only reports if a build
  // reached users with drift anyway (an emergency `git push` that bypassed the
  // gate). Fires once per boot, only for the active locale the user actually
  // sees, so volume is ~0. trackSilentProblem (not an exception): analytics-only,
  // the user is unaffected — the untranslated strings simply render in English.
  reportI18nUntranslatedStringsIfAny() {
    if (BOOT_LOCALE === 'en') return;
    const stat = I18N_FALLBACK_STATS[BOOT_LOCALE];
    if (!stat || stat.untranslated <= 0) return;
    this.logging.trackSilentProblem('i18nUntranslatedStringsShipped', {
      locale: BOOT_LOCALE,
      untranslated: stat.untranslated,
      total: stat.total,
      sampleKeys: stat.sampleKeys,
      buildVersion: AppVersion,
    });
  }

  /// if a previous boot was force-killed, report the native watchdog log to Sentry
  async reportPreviousFreezeIfAny() {
    if (!isCapacitorNative()) return;
    try {
      const { registerPlugin } = await import('@capacitor/core');
      const plugin = registerPlugin<{
        bootComplete(): Promise<{ hasPreviousLog: boolean; log?: string }>;
      }>('FreezeWatchdogPlugin');

      const result = await plugin.bootComplete();
      if (result.hasPreviousLog && result.log) {
        const fullLogLength = result.log.length;
        // #3797: the watchdog has no real freeze detection — it reports any
        // previous boot that never called bootComplete(). On Android the
        // dominant cause is a benign interrupted boot (the user backgrounds the
        // app during a slow cold start, or Android kills the backgrounded
        // process mid-boot), which drowned out the genuine foreground hangs.
        // classifyBootFreeze() separates them: real/unknown hangs stay on
        // FreezeWatchdog:BootLog (with the full attachment + a category in the
        // problem context, not a Sentry tag, so bucket counts need a per-event
        // sweep); a backgrounded/abandoned boot
        // is downgraded to the low-signal FreezeWatchdog:BootAbandonedBackground
        // key, keeping rate visibility without polluting BootLog.
        const decision = classifyBootFreeze(result.log);
        if (!decision.report) {
          // not the user's session going wrong: the classifier's own answer for a
          // backgrounded slow start is "do not report", and it is at problem tier only so
          // its RATE stays visible. Counted, it would stop the app asking for a rating after
          // every slow Android cold start, which is common enough to switch that off.
          this.logging.trackSilentProblem('FreezeWatchdog:BootAbandonedBackground', {
            category: decision.category,
            logLength: fullLogLength,
          }, undefined, SessionHealth.NotTheUsersSession);
          return;
        }

        // #3698: the old code put a ~50KB log string into the breadcrumb
        // context, which Sentry silently dropped — every BootLog event
        // arrived with empty context, making frozen boots undiagnosable.
        // Now the last ~50KB of the log travels as an event attachment
        // (attachments survive ingestion size limits), and a short tail goes
        // into the context for quick viewing in the issue UI.
        const maxAttachmentSize = 50_000;
        let log = result.log;
        if (log.length > maxAttachmentSize) {
          log = '...(truncated)\n' + log.slice(-maxAttachmentSize);
        }
        const maxContextTailSize = 2_000;
        const logTail = log.length > maxContextTailSize ? '...(see boot-freeze.log attachment)\n' + log.slice(-maxContextTailSize) : log;
        Sentry.withScope((scope) => {
          scope.addAttachment({ filename: 'boot-freeze.log', data: log, contentType: 'text/plain' });
          this.logging.trackSilentProblem('FreezeWatchdog:BootLog', { logTail, logLength: fullLogLength, category: decision.category });
        });
      }
    } catch (error) {
      this.logging.trackSilentProblem('FreezeWatchdog:BootCompleteFailed', { error: error?.message || String(error) });
    }
  }

  /**
   * Find out whether the CouchDB load balancer connection negotiated HTTP/2.
   *
   * NOT safe to run twice in the same boot: it appends an iframe per call. The
   * cold-start recovery path in `app/cold-start-recovery.ts` records this as one
   * of the reasons a cold start cannot simply be re-run.
   */
  probeHttp2Protocol() {
    // if offline or backgrounded, probe can't succeed — skip entirely
    if (this.onlineStateService.connectionState !== ConnectionState.Online || document.hidden) {
      return;
    }

    const couchUrl = AppEnvironment.pouchConfig.couch_serverurl;
    const protocolNames = { 'h2': 'HTTP/2', 'h3': 'HTTP/3', 'http/1.1': 'HTTP/1.1', 'http/1.0': 'HTTP/1.0' };

    // on Safari, the iframe-based CouchDB probe doesn't work — WebKit doesn't expose
    // nextHopProtocol in iframe PerformanceNavigationTiming (tested: allow-same-origin CSP,
    // PerformanceObserver — all return empty on iOS). Use the main page's own navigation
    // timing as a proxy: if the app server connection is h2, CouchDB LB is too.
    if (isBrowserSafari()) {
      const navEntries = performance.getEntriesByType('navigation') as PerformanceNavigationTiming[];
      const protocol = navEntries?.[0]?.nextHopProtocol || '';
      const protocolName = protocolNames[protocol] || protocol || 'unknown';
      console.log(`App: CouchDB protocol (Safari, via main page): ${protocolName}`);

      // if got a real non-h2 protocol, report it
      if (protocol && protocol !== 'h2' && protocol !== 'h3') {
        this.logging.trackSilentProblem('CouchDbNotHttp2', {
          protocol: protocolName, source: 'navigation', couchUrl,
          userAgent: navigator.userAgent,
          connectionState: this.onlineStateService.connectionState, documentHidden: document.hidden,
        });
      }
      return;
    }

    // non-Safari: use iframe probe for direct CouchDB protocol detection
    const probeUrl = couchUrl + 'global_common/_design/probe/probe.html';

    const iframe = document.createElement('iframe');
    iframe.style.display = 'none';
    iframe.src = probeUrl;

    let cleaned = false;

    const cleanup = () => {
      if (cleaned) return;
      cleaned = true;
      window.removeEventListener('message', messageHandler);
      if (iframe.parentNode) {
        iframe.parentNode.removeChild(iframe);
      }
    };

    const messageHandler = (ev: MessageEvent) => {
      // CouchDB serves attachments with CSP "sandbox allow-scripts", so the iframe
      // has an opaque origin ("null"). Validate via message shape instead.
      if (!ev.data || ev.data.type !== 'h2probe') return;

      const protocol = ev.data.protocol || 'unknown';
      const protocolName = protocolNames[protocol] || protocol;
      console.log(`App: CouchDB protocol: ${protocolName}`);

      // if not HTTP/2 or higher, report
      if (protocol !== 'h2' && protocol !== 'h3') {
        // nextHopProtocol returns empty in cross-origin sandboxed iframes across all browsers —
        // "unknown" is not actionable, only report actual protocol downgrades
        if (protocol === 'unknown') {
          console.log(`App: CouchDB protocol probe returned unknown (cross-origin sandbox limitation)`);
        } else {
          this.logging.trackSilentProblem('CouchDbNotHttp2', {
            protocol: protocolName, couchUrl, userAgent: navigator.userAgent,
            connectionState: this.onlineStateService.connectionState, documentHidden: document.hidden,
          });
        }
      }

      cleanup();
    };

    window.addEventListener('message', messageHandler);
    document.body.appendChild(iframe);

    // if no response within 10s, log and clean up (timeouts are not actionable)
    setTimeout(() => {
      if (!cleaned) {
        console.log('App: CouchDB protocol probe timed out');
        cleanup();
      }
    }, 10_000);
  }
}
